"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.builtInMiddleware = exports.Inngest = void 0;
const api_1 = require("../api/api");
const consts_1 = require("../helpers/consts");
const devserver_1 = require("../helpers/devserver");
const env_1 = require("../helpers/env");
const errors_1 = require("../helpers/errors");
const strings_1 = require("../helpers/strings");
const logger_1 = require("../middleware/logger");
const types_1 = require("../types");
const InngestFunction_1 = require("./InngestFunction");
const InngestMiddleware_1 = require("./InngestMiddleware");
/**
 * A client used to interact with the Inngest API by sending or reacting to
 * events.
 *
 * To provide event typing, see {@link EventSchemas}.
 *
 * ```ts
 * const inngest = new Inngest({ name: "My App" });
 *
 * // or to provide event typing too
 * const inngest = new Inngest({
 *   name: "My App",
 *   schemas: new EventSchemas().fromRecord<{
 *     "app/user.created": {
 *       data: { userId: string };
 *     };
 *   }>(),
 * });
 * ```
 *
 * @public
 */
class Inngest {
    get apiBaseUrl() {
        return this._apiBaseUrl;
    }
    get eventBaseUrl() {
        return this._eventBaseUrl;
    }
    get env() {
        var _a;
        return (_a = this.headers[consts_1.headerKeys.Environment]) !== null && _a !== void 0 ? _a : null;
    }
    /**
     * A client used to interact with the Inngest API by sending or reacting to
     * events.
     *
     * To provide event typing, see {@link EventSchemas}.
     *
     * ```ts
     * const inngest = new Inngest({ name: "My App" });
     *
     * // or to provide event typing too
     * const inngest = new Inngest({
     *   name: "My App",
     *   schemas: new EventSchemas().fromRecord<{
     *     "app/user.created": {
     *       data: { userId: string };
     *     };
     *   }>(),
     * });
     * ```
     */
    constructor(options) {
        /**
         * Inngest event key, used to send events to Inngest Cloud.
         */
        this.eventKey = "";
        /**
         * The absolute URL of the Inngest Cloud API.
         */
        this.sendEventUrl = new URL(`e/${this.eventKey}`, consts_1.defaultInngestEventBaseUrl);
        this.createFunction = (rawOptions, rawTrigger, handler) => {
            const options = this.sanitizeOptions(rawOptions);
            const triggers = this.sanitizeTriggers(rawTrigger);
            return new InngestFunction_1.InngestFunction(this, Object.assign(Object.assign({}, options), { triggers }), handler);
        };
        this.options = options;
        const { id, fetch, logger = new logger_1.DefaultLogger(), middleware, isDev, } = this.options;
        if (!id) {
            // TODO PrettyError
            throw new Error("An `id` must be passed to create an Inngest instance.");
        }
        this.id = id;
        this._mode = (0, env_1.getMode)({
            explicitMode: typeof isDev === "boolean" ? (isDev ? "dev" : "cloud") : undefined,
        });
        this.fetch = (0, env_1.getFetch)(fetch);
        this.inngestApi = new api_1.InngestApi({
            baseUrl: this.apiBaseUrl,
            signingKey: (0, env_1.processEnv)(consts_1.envKeys.InngestSigningKey) || "",
            signingKeyFallback: (0, env_1.processEnv)(consts_1.envKeys.InngestSigningKeyFallback),
            fetch: this.fetch,
            mode: this.mode,
        });
        this.loadModeEnvVars();
        this.logger = logger;
        this.middleware = this.initializeMiddleware([
            ...exports.builtInMiddleware,
            ...(middleware || []),
        ]);
    }
    /**
     * Set the environment variables for this client. This is useful if you are
     * passed environment variables at runtime instead of as globals and need to
     * update the client with those values as requests come in.
     */
    setEnvVars(env = (0, env_1.allProcessEnv)()) {
        this.mode = (0, env_1.getMode)({ env, client: this });
        return this;
    }
    loadModeEnvVars() {
        this._apiBaseUrl =
            this.options.baseUrl ||
                this.mode["env"][consts_1.envKeys.InngestApiBaseUrl] ||
                this.mode["env"][consts_1.envKeys.InngestBaseUrl] ||
                this.mode.getExplicitUrl(consts_1.defaultInngestApiBaseUrl);
        this._eventBaseUrl =
            this.options.baseUrl ||
                this.mode["env"][consts_1.envKeys.InngestEventApiBaseUrl] ||
                this.mode["env"][consts_1.envKeys.InngestBaseUrl] ||
                this.mode.getExplicitUrl(consts_1.defaultInngestEventBaseUrl);
        this.setEventKey(this.options.eventKey || this.mode["env"][consts_1.envKeys.InngestEventKey] || "");
        this.headers = (0, env_1.inngestHeaders)({
            inngestEnv: this.options.env,
            env: this.mode["env"],
        });
        this.inngestApi["mode"] = this.mode;
        this.inngestApi["apiBaseUrl"] = this._apiBaseUrl;
    }
    /**
     * Initialize all passed middleware, running the `register` function on each
     * in sequence and returning the requested hook registrations.
     */
    async initializeMiddleware(middleware = [], opts) {
        var _a;
        /**
         * Wait for the prefix stack to run first; do not trigger ours before this
         * is complete.
         */
        const prefix = await ((_a = opts === null || opts === void 0 ? void 0 : opts.prefixStack) !== null && _a !== void 0 ? _a : []);
        const stack = middleware.reduce(async (acc, m) => {
            // Be explicit about waiting for the previous middleware to finish
            const prev = await acc;
            const next = await m.init(Object.assign({ client: this }, opts === null || opts === void 0 ? void 0 : opts.registerInput));
            return [...prev, next];
        }, Promise.resolve([]));
        return [...prefix, ...(await stack)];
    }
    get mode() {
        return this._mode;
    }
    set mode(m) {
        this._mode = m;
        this.loadModeEnvVars();
    }
    /**
     * Given a response from Inngest, relay the error to the caller.
     */
    async getResponseError(response, foundErr = "Unknown error") {
        let errorMessage = foundErr;
        if (errorMessage === "Unknown error") {
            switch (response.status) {
                case 401:
                    errorMessage = "Event key Not Found";
                    break;
                case 400:
                    errorMessage = "Cannot process event payload";
                    break;
                case 403:
                    errorMessage = "Forbidden";
                    break;
                case 404:
                    errorMessage = "Event key not found";
                    break;
                case 406:
                    errorMessage = `${JSON.stringify(await response.json())}`;
                    break;
                case 409:
                case 412:
                    errorMessage = "Event transformation failed";
                    break;
                case 413:
                    errorMessage = "Event payload too large";
                    break;
                case 500:
                    errorMessage = "Internal server error";
                    break;
                default:
                    errorMessage = await response.text();
                    break;
            }
        }
        return new Error(`Inngest API Error: ${response.status} ${errorMessage}`);
    }
    /**
     * Set the event key for this instance of Inngest. This is useful if for some
     * reason the key is not available at time of instantiation or present in the
     * `INNGEST_EVENT_KEY` environment variable.
     */
    setEventKey(
    /**
     * Inngest event key, used to send events to Inngest Cloud. Use this is your
     * key is for some reason not available at time of instantiation or present
     * in the `INNGEST_EVENT_KEY` environment variable.
     */
    eventKey) {
        this.eventKey = eventKey || consts_1.dummyEventKey;
        this.sendEventUrl = new URL(`e/${this.eventKey}`, this.eventBaseUrl || consts_1.defaultInngestEventBaseUrl);
    }
    eventKeySet() {
        return Boolean(this.eventKey) && this.eventKey !== consts_1.dummyEventKey;
    }
    /**
     * Send one or many events to Inngest. Takes an entire payload (including
     * name) as each input.
     *
     * ```ts
     * await inngest.send({ name: "app/user.created", data: { id: 123 } });
     * ```
     *
     * Returns a promise that will resolve if the event(s) were sent successfully,
     * else throws with an error explaining what went wrong.
     *
     * If you wish to send an event with custom types (i.e. one that hasn't been
     * generated), make sure to add it when creating your Inngest instance, like
     * so:
     *
     * ```ts
     * const inngest = new Inngest({
     *   name: "My App",
     *   schemas: new EventSchemas().fromRecord<{
     *     "my/event": {
     *       name: "my/event";
     *       data: { bar: string };
     *     };
     *   }>(),
     * });
     * ```
     */
    async send(payload, options) {
        const headers = Object.assign({}, ((options === null || options === void 0 ? void 0 : options.env) ? { [consts_1.headerKeys.Environment]: options.env } : {}));
        return this._send({ payload, headers });
    }
    /**
     * Internal method for sending an event, used to allow Inngest internals to
     * further customize the request sent to an Inngest Server.
     */
    async _send({ payload, headers, }) {
        var _a;
        const hooks = await (0, InngestMiddleware_1.getHookStack)(this.middleware, "onSendEvent", undefined, {
            transformInput: (prev, output) => {
                return Object.assign(Object.assign({}, prev), output);
            },
            transformOutput(prev, output) {
                return {
                    result: Object.assign(Object.assign({}, prev.result), output === null || output === void 0 ? void 0 : output.result),
                };
            },
        });
        let payloads = Array.isArray(payload)
            ? payload
            : payload
                ? [payload]
                : [];
        const inputChanges = await ((_a = hooks.transformInput) === null || _a === void 0 ? void 0 : _a.call(hooks, {
            payloads: [...payloads],
        }));
        if (inputChanges === null || inputChanges === void 0 ? void 0 : inputChanges.payloads) {
            payloads = [...inputChanges.payloads];
        }
        // Ensure that we always add "ts" and "data" fields to events. "ts" is auto-
        // filled by the event server so is safe, and adding here fixes Next.js
        // server action cache issues.
        payloads = payloads.map((p) => {
            return Object.assign(Object.assign({}, p), { ts: p.ts || new Date().getTime(), 
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                data: p.data || {} });
        });
        const applyHookToOutput = async (arg) => {
            var _a;
            const hookOutput = await ((_a = hooks.transformOutput) === null || _a === void 0 ? void 0 : _a.call(hooks, arg));
            return Object.assign(Object.assign({}, arg.result), hookOutput === null || hookOutput === void 0 ? void 0 : hookOutput.result);
        };
        /**
         * It can be valid for a user to send an empty list of events; if this
         * happens, show a warning that this may not be intended, but don't throw.
         */
        if (!payloads.length) {
            console.warn((0, errors_1.prettyError)({
                type: "warn",
                whatHappened: "`inngest.send()` called with no events",
                reassurance: "This is not an error, but you may not have intended to do this.",
                consequences: "The returned promise will resolve, but no events have been sent to Inngest.",
                stack: true,
            }));
            return await applyHookToOutput({ result: { ids: [] } });
        }
        // When sending events, check if the dev server is available.  If so, use the
        // dev server.
        let url = this.sendEventUrl.href;
        /**
         * If in prod mode and key is not present, fail now.
         */
        if (this.mode.isCloud && !this.eventKeySet()) {
            throw new Error((0, errors_1.prettyError)({
                whatHappened: "Failed to send event",
                consequences: "Your event or events were not sent to Inngest.",
                why: "We couldn't find an event key to use to send events to Inngest.",
                toFixNow: errors_1.fixEventKeyMissingSteps,
            }));
        }
        /**
         * If dev mode has been inferred, try to hit the dev server first to see if
         * it exists. If it does, use it, otherwise fall back to whatever server we
         * have configured.
         *
         * `INNGEST_BASE_URL` is used to set both dev server and prod URLs, so if a
         * user has set this it means they have already chosen a URL to hit.
         */
        if (this.mode.isDev && this.mode.isInferred && !this.eventBaseUrl) {
            const devAvailable = await (0, devserver_1.devServerAvailable)(consts_1.defaultDevServerHost, this.fetch);
            if (devAvailable) {
                url = (0, devserver_1.devServerUrl)(consts_1.defaultDevServerHost, `e/${this.eventKey}`).href;
            }
        }
        // We don't need to do fallback auth here because this uses event keys and
        // not signing keys
        const response = await this.fetch(url, {
            method: "POST",
            body: (0, strings_1.stringify)(payloads),
            headers: Object.assign(Object.assign({}, this.headers), headers),
        });
        let body;
        try {
            const rawBody = await response.json();
            body = await types_1.sendEventResponseSchema.parseAsync(rawBody);
        }
        catch (err) {
            throw await this.getResponseError(response);
        }
        if (body.status / 100 !== 2 || body.error) {
            throw await this.getResponseError(response, body.error);
        }
        return await applyHookToOutput({ result: { ids: body.ids } });
    }
    /**
     * Runtime-only validation.
     */
    sanitizeOptions(options) {
        if (Object.prototype.hasOwnProperty.call(options, "fns")) {
            // v2 -> v3 migration warning
            console.warn(`${consts_1.logPrefix} InngestFunction: \`fns\` option has been deprecated in v3; use \`middleware\` instead. See https://www.inngest.com/docs/sdk/migration`);
        }
        if (typeof options === "string") {
            // v2 -> v3 runtime migraton warning
            console.warn(`${consts_1.logPrefix} InngestFunction: Creating a function with a string as the first argument has been deprecated in v3; pass an object instead. See https://www.inngest.com/docs/sdk/migration`);
            return { id: options };
        }
        return options;
    }
    /**
     * Runtime-only validation.
     */
    sanitizeTriggers(triggers) {
        if (typeof triggers === "string") {
            // v2 -> v3 migration warning
            console.warn(`${consts_1.logPrefix} InngestFunction: Creating a function with a string as the second argument has been deprecated in v3; pass an object instead. See https://www.inngest.com/docs/sdk/migration`);
            return [{ event: triggers }];
        }
        if (!Array.isArray(triggers)) {
            return [triggers];
        }
        return triggers;
    }
}
exports.Inngest = Inngest;
/**
 * Default middleware that is included in every client, placed after the user's
 * middleware on the client but before function-level middleware.
 *
 * It is defined here to ensure that comments are included in the generated TS
 * definitions. Without this, we infer the stack of built-in middleware without
 * comments, losing a lot of value.
 *
 * If this is moved, please ensure that using this package in another project
 * can correctly access comments on mutated input and output.
 *
 * This return pattern mimics the output of a `satisfies` suffix; it's used as
 * we support versions of TypeScript prior to the introduction of `satisfies`.
 */
exports.builtInMiddleware = ((m) => m)([
    new InngestMiddleware_1.InngestMiddleware({
        name: "Inngest: Logger",
        init({ client }) {
            return {
                onFunctionRun(arg) {
                    const { ctx } = arg;
                    const metadata = {
                        runID: ctx.runId,
                        eventName: ctx.event.name,
                        functionName: arg.fn.name,
                    };
                    let providedLogger = client["logger"];
                    // create a child logger if the provided logger has child logger implementation
                    try {
                        if ("child" in providedLogger) {
                            providedLogger = providedLogger.child(metadata);
                        }
                    }
                    catch (err) {
                        console.error('failed to create "childLogger" with error: ', err);
                        // no-op
                    }
                    const logger = new logger_1.ProxyLogger(providedLogger);
                    return {
                        transformInput() {
                            return {
                                ctx: {
                                    /**
                                     * The passed in logger from the user.
                                     * Defaults to a console logger if not provided.
                                     */
                                    logger: logger,
                                },
                            };
                        },
                        beforeExecution() {
                            logger.enable();
                        },
                        transformOutput({ result: { error } }) {
                            if (error) {
                                logger.error(error);
                            }
                        },
                        async beforeResponse() {
                            await logger.flush();
                        },
                    };
                },
            };
        },
    }),
]);
//# sourceMappingURL=Inngest.js.map